/* eslint-disable no-unused-vars */
import { useCallback, useState } from 'react'
import { useNavigate, NavigateFunction } from 'react-router-dom'
import moment from 'moment'

import {
  DealsDealStatusChoices,
  useDealQuery,
  useProposalQuery,
} from '@/apollo/graphql'
import { IDeal, TFormSubmit } from '@/types'
import { useAPI } from '@/hooks/index'
import { homePath, transformData } from '@/utils/index'

interface IUseData {
  id?: string
  proposalId?: string
}

interface IUseDataRes {
  loading: boolean
  submitting: boolean
  data: Partial<IDeal>
  refetch: () => void
  onSubmit: TFormSubmit<IDeal>
  onCancel: () => void
}

const defaultData: Partial<IDeal> = {
  status: DealsDealStatusChoices.InCreation,
  npv: null,
}

export const useData = ({ id, proposalId }: IUseData): IUseDataRes => {
  const navigate: NavigateFunction = useNavigate()
  const { submitting, sendForm } = useAPI()
  const [data, setData] = useState<any>(defaultData)

  const { loading, refetch } = useDealQuery({
    skip: !id,
    fetchPolicy: 'network-only',
    variables: {
      id: id || '',
    },
    onCompleted: res => {
      if (res.deal) {
        setData({
          ...res.deal,
          status: res.deal.status || DealsDealStatusChoices.InCreation,
        })
      }
    },
  })

  const { loading: loadingProposal } = useProposalQuery({
    skip: !!id || !proposalId,
    variables: {
      id: proposalId || '',
    },
    onCompleted: res => {
      if (res.proposal) {
        const _data: any = {
          ...res.proposal,
          installations:
            res.proposal.installations?.edges
              ?.map(edge => edge?.node)
              .filter(Boolean)
              .map(installation => ({
                id: installation?.id,
                name: installation?.name,
                price_without_vat: installation?.priceWithoutVat,
                installation_type: installation?.installationType,
                is_customer_paid: installation?.isCustomerPaid,
              })) || [],
          id: undefined,
          proposal: {
            id: res.proposal.id,
          },
        }
        setData((prev: any) => ({ ...prev, ..._data }))
      }
    },
  })

  const onSubmit: TFormSubmit<IDeal> = useCallback(
    (path, isEdit, callback) => d => {
      if (d.startDate) {
        d.startDate = moment(d.startDate).format('YYYY-MM-DD')
      }
      if (d.endDate) {
        d.endDate = moment(d.endDate).format('YYYY-MM-DD')
      }

      sendForm(
        {
          path,
          method: isEdit ? 'PUT' : 'POST',
          data: transformData({
            ...d,
            npv: (d.npv as unknown) === '' || d.npv == null ? null : d.npv,
          }),
          callback,
        },
        { showNotify: true },
      )
    },
    [sendForm],
  )

  const onCancel = useCallback(() => {
    navigate(homePath)
  }, [navigate])

  return {
    loading: loading || loadingProposal,
    submitting,
    data,
    refetch,
    onSubmit,
    onCancel,
  }
}
